/**
 * 교사·원장 배정 운영 화면(DEV-I-027, 최소 구성): 반 선택 → 배정 만들기 → 배정별 제출 현황 → (원장) 채점·근거 배정·단어 수집 실행.
 * 화면은 권한을 판단하지 않고 서버 응답(403·404)만 표시한다. 자동 실행·자동 승인은 없고 모든 실행은 버튼을 누를 때 한 번이다.
 * 만든 배정은 검증용이며 학생 배포 허용은 바뀌지 않는다(서버가 정한다).
 * 요청 키: 결과를 못 받은 요청은 같은 키·같은 내용으로만 다시 보낸다. 서버가 확정 응답을 준 뒤에만 키를 버린다. 키 충돌에 새 키를 자동으로 만들지 않는다.
 */
import { h, clear, toast } from './dom.js';
import { currentGen, newRequestKey, type ApiResult, type Transport } from '../core/transport.js';
import type { ItemView } from '../core/types.js';
import { mountTargetPanel } from './target-panel.js';
import { mountPdfImport } from './pdf-import.js';
import { mountPdfReupload } from './pdf-reupload.js';
import { mountProfileCorrection } from './profile-correction.js';
import { mountInterventions } from './interventions.js';

interface Named { id: string; name: string }
interface Release { id: string; title: string; version: number; itemCount: number }
interface AsgRow { id: string; title: string; purpose: string; release: { title: string; version: number }; inProgress: number; submitted: number }
interface AttemptRow { attemptId: string; studentName: string; attemptNo: number; status: string; submittedAt: string | null; gradingRecords: number }

type PreviewItem = ItemView;
interface ReviewPoint { pointId: string; displayNumber: string; gr: { state: string; result?: string; reason?: string }; assignments: { status: string }[] }
interface WordSource { displayNumber: string; validity: string; sense: { headword: string } }

const STATUS: Record<string, string> = { 'in-progress': '푸는 중', submitted: '제출 완료', void: '무효' };

export function mountTeacherOps(root: HTMLElement, t: Transport, role: 'director' | 'teacher', logout: () => void): void {
  const gen = currentGen();
  const live = () => gen === currentGen();
  /** 미확정 요청의 키 보관(작업+내용 → 키) */
  const pendingKeys = new Map<string, string>();
  const msg = h('p', { role: 'status', 'data-testid': 'ops-msg' });
  const say = (s: string) => { msg.textContent = s; };
  const classSel = h('select', { 'data-testid': 'ops-class', onchange: () => void loadAssignments() });
  const relSel = h('select', { 'data-testid': 'ops-release' });
  const title = h('input', { 'data-testid': 'ops-title', required: true, maxlength: '200' });
  const unans = h('input', { type: 'checkbox', 'data-testid': 'ops-unans' });
  const list = h('div', { 'data-testid': 'ops-assignments' });
  const att = h('div', { 'data-testid': 'ops-attempts' });

  /** 같은 작업·같은 내용이면 같은 키. 확정 응답(HTTP)을 받으면 키를 버린다 */
  async function send(op: string, path: string, body: unknown): Promise<ApiResult> {
    const id = `${op}|${path}|${JSON.stringify(body)}`;
    const key = pendingKeys.get(id) ?? newRequestKey();
    pendingKeys.set(id, key);
    const r = await t.request('POST', path, { body, key });
    if (r.kind === 'http') pendingKeys.delete(id);
    return r;
  }
  const failText = (r: ApiResult): string => r.kind === 'network' ? '결과를 받지 못했어요. 같은 버튼을 다시 누르면 같은 요청으로 확인해요.'
    : r.status === 403 ? '이 계정은 할 수 없는 작업이에요.' : r.status === 404 ? '대상을 찾을 수 없어요.' : r.status === 401 ? '다시 로그인해 주세요.'
    : `실행하지 못했어요(${String(r.body['error'] ?? r.status)}).`;

  async function loadAttempts(asgId: string): Promise<void> {
    clear(att);
    const r = await t.request('GET', `/admin/assignments/${asgId}/attempts`);
    if (!live()) return;
    if (r.kind !== 'http' || r.status !== 200) { say(failText(r)); return; }
    const rows = r.body['attempts'] as AttemptRow[];
    att.append(h('h3', {}, `제출 현황 · ${String(r.body['assignment']?.title ?? '')}`),
      rows.length === 0 ? h('p', { 'data-testid': 'ops-attempts-empty' }, '아직 시작한 학생이 없어요.') : h('ul', {}, ...rows.map((a) => {
        const run = (label: string, path: string, tid: string) => h('button', {
          type: 'button', 'data-testid': `${tid}-${a.attemptId}`,
          onclick: async () => {
            const res = await send(tid, path, {});
            if (!live()) return;
            if (res.kind === 'http' && res.status < 300) { say(`${label} 실행 결과: ${JSON.stringify(res.body)}`); void loadAttempts(asgId); } else say(failText(res));
          },
        }, label);
        return h('li', { 'data-testid': `ops-attempt-${a.attemptId}` },
          `${a.studentName} · ${a.attemptNo}회 · ${STATUS[a.status] ?? a.status}${a.submittedAt !== null ? ` · ${a.submittedAt}` : ''} · 채점 기록 ${a.gradingRecords}`,
          // 후속 운영은 원장만(서버 권한). 제출된 시도에서만 누를 수 있다.
          role === 'director' && a.status === 'submitted' ? [
            h('button', { type: 'button', 'data-testid': `review-${a.attemptId}`, onclick: () => void loadReview(a.attemptId) }, '결과·근거·단어 보기'),
            run('채점 실행', `/admin/attempts/${a.attemptId}/grading-runs`, 'grade'),
            run('근거 문제 배정', `/admin/attempts/${a.attemptId}/evidence-assignments`, 'evidence'),
            run('단어 수집', `/admin/attempts/${a.attemptId}/word-collections`, 'words'),
          ] : null);
      })));
  }

  async function loadAssignments(): Promise<void> {
    clear(list); clear(att);
    if (classSel.value === '') return;
    const r = await t.request('GET', `/admin/assignments?classId=${encodeURIComponent(classSel.value)}`);
    if (!live()) return;
    if (r.kind !== 'http' || r.status !== 200) { say(failText(r)); return; }
    const rows = r.body['assignments'] as AsgRow[];
    list.append(h('h3', {}, '이 반의 배정'), rows.length === 0 ? h('p', { 'data-testid': 'ops-empty' }, '아직 배정이 없어요.') : h('ul', {}, ...rows.map((a) =>
      h('li', {}, h('button', { type: 'button', 'data-testid': `ops-open-${a.id}`, onclick: () => void loadAttempts(a.id) },
        `${a.title} · ${a.release.title} v${a.release.version} · 푸는 중 ${a.inProgress} · 제출 ${a.submitted}${a.purpose === 'verification' ? ' · 검증용' : ''}`),
        // 원장만(서버 content.view). 읽기 전용이라 학생 로그인·시도·응답·제공 기록이 생기지 않는다.
        role === 'director' ? [' ', h('button', { type: 'button', 'data-testid': `ops-preview-${a.id}`, onclick: () => void loadPreview(a.id) }, '풀이 미리보기(읽기 전용)')] : null))));
  }

  const pane = h('div', { 'data-testid': 'ops-preview' });
  async function loadPreview(asgId: string): Promise<void> {
    clear(pane);
    const r = await t.request('GET', `/admin/assignments/${asgId}/preview`);
    if (!live()) return;
    if (r.kind !== 'http' || r.status !== 200) { say(failText(r)); return; }
    const items = r.body['items'] as PreviewItem[];
    pane.append(h('h3', {}, `풀이 미리보기 · ${String(r.body['assignment']?.title ?? '')}`),
      h('p', { 'data-testid': 'ops-preview-note' }, '읽기 전용이에요. 보기는 눌러도 저장되지 않고 학생 기록·제공 기록도 만들지 않아요. 정답은 표시하지 않아요.'),
      ...items.map((it) => h('section', { 'data-testid': `ops-preview-item-${it.number}` },
        h('h4', {}, `${it.number}번${it.sectionLabel !== null ? ` · ${it.sectionLabel}` : ''}`),
        it.responseMode === 'single-choice'
          ? [h('p', {}, it.stem ?? ''), h('ol', {}, ...it.options.map((o) => h('li', {}, o)))]
          : [h('p', {}, ...(it.segments ?? []).map((s) => s.kind === 'text' ? s.text : `[${it.grPoints.find((p) => p.id === s.pointId)?.displayNumber ?? '?'}]`)),
            h('ul', {}, ...it.grPoints.map((p) => h('li', {}, `${p.displayNumber}: ${p.options.map((o) => o.label).join(' / ')}`)))])));
  }

  const review = h('div', { 'data-testid': 'ops-review' });
  /** 원장 읽기 전용 조회(기존 GET): 채점 상태·근거 문제 배정·수집된 단어. 제공 기록을 만들지 않는다. */
  async function loadReview(attemptId: string): Promise<void> {
    clear(review);
    const [ev, wd] = await Promise.all([t.request('GET', `/admin/attempts/${attemptId}/evidence-assignments`), t.request('GET', `/admin/attempts/${attemptId}/word-collections`)]);
    if (!live()) return;
    if (ev.kind !== 'http' || ev.status !== 200) { say(failText(ev)); return; }
    const pts = ev.body['points'] as ReviewPoint[];
    review.append(h('h3', {}, '결과·근거 문제 (읽기 전용)'),
      pts.length === 0 ? h('p', { 'data-testid': 'ops-review-empty' }, '제출된 지점이 없어요.') : h('ul', { 'data-testid': 'ops-review-points' }, ...pts.map((p) =>
        h('li', {}, `${p.displayNumber}: ${p.gr.state === 'graded' ? (p.gr.result === 'correct' ? '정답' : '오답') : `결과 대기${p.gr.reason ? `(${p.gr.reason})` : ''}`} · 근거 문제 배정 ${p.assignments.length}건${p.assignments.map((x) => ` [${x.status}]`).join('')}`))));
    if (wd.kind !== 'http' || wd.status !== 200) { review.append(h('p', { 'data-testid': 'ops-words-error' }, '수집된 단어를 불러오지 못했어요.')); return; }
    const src = wd.body['sources'] as WordSource[];
    review.append(h('h3', {}, '수집된 단어 (읽기 전용)'), src.length === 0 ? h('p', { 'data-testid': 'ops-words-empty' }, '수집된 단어가 없어요(수집을 아직 실행하지 않았거나 대상이 없음).') : h('ul', { 'data-testid': 'ops-words' }, ...src.map((x) =>
      h('li', {}, `${x.displayNumber}: ${x.sense.headword}${x.validity === 'valid' ? '' : ` · ${x.validity}`}`))));
  }

  const form = h('form', {
    'data-testid': 'ops-create',
    onsubmit: async (e: Event) => {
      e.preventDefault();
      if (classSel.value === '' || relSel.value === '') { say('반과 문제지를 골라 주세요.'); return; }
      const res = await send('create', '/admin/assignments', { classId: classSel.value, releaseId: relSel.value, title: title.value.trim(), allowUnanswered: unans.checked, inputChannels: ['screen'] });
      if (!live()) return;
      if (res.kind === 'http' && res.status === 201) { say('배정을 만들었어요. 검증용 배정이며 학생 배포 허용은 아직 꺼져 있어요.'); toast('배정을 만들었어요.'); void loadAssignments(); } else say(failText(res));
    },
  }, h('h3', {}, '배정 만들기'),
  h('label', {}, '반 ', classSel), h('label', {}, ' 문제지 ', relSel), h('label', {}, ' 이름 ', title),
  h('label', {}, unans, ' 답하지 않은 문항이 있어도 제출 허용'), h('button', { class: 'primary', type: 'submit', 'data-testid': 'ops-create-submit' }, '배정 만들기'));

  /** 반별 제공 설정 한 번(원장): 선택한 반에 확정 자료 여러 개를 한 번에 배정. 학생은 자기 반 목록에서 직접 고른다. */
  const provList = h('div', { 'data-testid': 'ops-prov-list' });
  const provision = h('section', { 'data-testid': 'ops-provision' }, h('h3', {}, '이 반에 자료 한 번에 제공'),
    h('p', {}, '위에서 고른 반에 자료를 한 번에 연결해요. 이미 연결된 자료는 새로 만들지 않아요. 검증용 배정이며 학생 배포 허용은 바뀌지 않아요.'), provList,
    h('button', { type: 'button', class: 'primary', 'data-testid': 'ops-prov-submit', onclick: async () => {
      const ids = [...provList.querySelectorAll<HTMLInputElement>('input:checked')].map((x) => x.value).sort();
      if (classSel.value === '' || ids.length === 0) { say('반과 제공할 자료를 골라 주세요.'); return; }
      const res = await send('provision', '/admin/class-provisions', { classId: classSel.value, releaseIds: ids, allowUnanswered: unans.checked });
      if (!live()) return;
      if (res.kind === 'http' && res.status === 201) {
        say(`제공 설정을 마쳤어요. 새로 연결 ${(res.body['created'] as unknown[]).length}건, 이미 연결됨 ${(res.body['alreadyProvided'] as unknown[]).length}건. 학생 배포 허용은 아직 꺼져 있어요.`);
        void loadAssignments();
      } else say(failText(res));
    } }, '선택한 자료를 이 반에 제공'));

  clear(root);
  const ivHost = h('div', { 'data-testid': 'iv-host' }); // 개입 필요 학생을 첫 화면 맨 위에(전체 현황은 상세 조회)
  mountInterventions(ivHost, t);
  const targetHost = h('div');
  mountTargetPanel(targetHost, t, role);
  // 교사는 문제지 목록 권한이 없어 배정을 만들 수 없다(권한 유지). 만들기 폼은 숨기고 제출 현황 조회만 제공한다.
  const teacherNote = h('p', { 'data-testid': 'ops-teacher-note' }, '교사 계정은 담당 반의 배정과 제출 현황을 볼 수 있어요. 배정 만들기는 원장이 해요.');
  root.append(h('header', {}, h('p', { class: 'brand-text' }, '그로잉어학원'), h('h1', {}, '배정·학습 운영'),
    role === 'director' ? h('a', { href: '/', 'data-testid': 'nav-review' }, '문항 등록·검수') : null, h('button', { type: 'button', onclick: logout }, '로그아웃')),
    h('main', {}, ivHost, msg, role === 'director' ? [provision, form] : [teacherNote, h('label', {}, '반 ', classSel)], targetHost, list, pane, att, review, mountProfileCorrection(t, classSel), role === 'teacher' ? mountPdfImport(t, 'teacher') : null, role === 'teacher' ? mountPdfReupload(t) : null,
      h('p', { 'data-testid': 'ops-limits' }, '안내: 풀이 미리보기와 결과·근거·단어 조회는 원장용 읽기 전용이에요(학생으로 로그인하거나 학생 기록을 바꾸지 않아요). 학생이 보는 실제 화면 확인은 검증용 학생 계정으로 해요. PDF 자료 등록·원문 대조는 문항 등록·검수 화면에서 해요. 검수 상태 요약 목록은 아직 지원하지 않아요.')));

  void (async () => {
    const c = await t.request('GET', '/admin/classes');
    if (!live()) return;
    if (c.kind !== 'http' || c.status !== 200) { say(failText(c)); return; }
    classSel.append(h('option', { value: '' }, '반 선택'), ...(c.body['classes'] as Named[]).map((x) => h('option', { value: x.id }, x.name)));
    if (role !== 'director') return;
    const r = await t.request('GET', '/admin/releases');
    if (!live()) return;
    if (r.kind === 'http' && r.status === 200) {
      const rels = r.body['releases'] as Release[];
      relSel.append(h('option', { value: '' }, '문제지 선택'), ...rels.map((x) => h('option', { value: x.id }, `${x.title} v${x.version} (${x.itemCount}문항)`)));
      provList.append(...rels.map((x) => h('label', { class: 'prov-row' }, h('input', { type: 'checkbox', value: x.id }), ` ${x.title} v${x.version} (${x.itemCount}문항)`)));
    }
    else say(failText(r));
  })();
}
